import React, { memo, useMemo } from 'react';
import { StyleSheet, View, StyleProp, ViewStyle, TextStyle, Text } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { MB_ButtonProps } from '../../../general/button';
import { COLORS } from './constants';
import { isMobile } from '../../../../utils/general/device-info';
import { MB_ModalUtilBackdrop, MB_ModalUtilBackdropProps } from './MB_ModalUtilBackdrop';
import { MB_ModalUtilCloseButton } from './MB_ModalUtilCloseButton';
import { MB_ModalChildren } from './typesAndInterfaces';
import { MB_ModalUtilChildWrapperScrollView } from './MB_ModalUtilChildWrapperScrollView/MB_ModalUtilChildWrapperScrollView';

interface MB_ModalUtilChildWrapperProps extends Omit<MB_ModalUtilBackdropProps, 'onDismissInProgress'> {
  /**
   * The modal's content.
   */
  children?: MB_ModalChildren;

  /**
   * Style for the view that wraps the modal's children.
   */
  childrenWrapperStyle?: StyleProp<ViewStyle>;

  /**
   * Content container style for the scroll view that wraps the child if `simplified` is not true.
   */
  scrollViewContentContainerStyle?: StyleProp<ViewStyle>;

  /**
   * Content container style for the non-scroll view that wraps the child if `simplified` is not true.
   */
  withoutScrollViewContainer?: StyleProp<ViewStyle>;

  /**
   * Called when the close button is pressed.
   *
   * If you just want to detect if the modal was dismissed, use `onDismiss` instead.
   * `onCloseButtonPressed` is called before the dismiss animation happens and is intended
   * to detect that the modal closed because of the user pressing the close button.
   */
  onCloseButtonPressed?: () => void;

  /**
   * Hides the close button that is shown by default when using a not simplified modal.
   */
  hideCloseButton?: boolean;

  /**
   * Button props for the default close button.
   */
  closeButtonProps?: MB_ButtonProps;

  /**
   * Controls the color and size for the close button's X symbol.
   */
  closeButtonXProps?: { size?: number; color?: string };

  /**
   * You can pass a custom close button component instead of using the default one.
   * Note that you will need to handle the modal close yourself if you do this (most likely by
   * calling `modalRef.hideModal()` to allow the animation to play out).
   */
  closeButton?: React.JSX.Element;

  /**
   * Title that is shown by default when using a not simplified modal.
   */
  title?: string;

  /**
   * Title style that is shown by default when using a not simplified modal.
   */
  titleStyle?: StyleProp<TextStyle>;

  /**
   * Custom title component that is shown by default when using a not simplified modal.
   */
  titleCustomComponent?: React.JSX.Element;

  /**
   * If simplified is set to true, the children are just wrapped in a simple view instead of
   * adding a scrollview, title, close button and so forth.
   */
  simplified?: boolean;

  /**
   * Presents the children without a scrollview that is used in not simplified version of the modal.
   */
  withoutScrollView?: boolean;

  keyboardAwareProps?: {
    /**
     * Adds an extra offset to the keyboard. Useful if you want to stick elements
     * above the keyboard.
     */
    extraScrollHeight?: number;
  };

  /**
   * Determines when the keyboard should stay visible after a tap.
   *
   *  - 'never' (default), tapping outside of the focused text input when the keyboard
   * is up dismisses the keyboard. When this happens, children won't receive the tap.
   *  - 'always', the keyboard will not dismiss automatically, and the scroll view will
   * not catch taps, but children of the scroll view can catch taps.
   *  - 'handled', the keyboard will not dismiss automatically when the tap was handled
   * by a children, (or captured by an ancestor).
   *  - false, deprecated, use 'never' instead.
   *  - true, deprecated, use 'always' instead.
   */
  keyboardShouldPersistTaps?: boolean | 'always' | 'never' | 'handled' | undefined;

  /**
   * Style for the title wrapper.
   */
  titleWrapperStyle?: StyleProp<ViewStyle>;
}

interface MB_ModalUtilChildWrapperPropsInternal extends MB_ModalUtilChildWrapperProps {
  /**
   * A callback that will be called when the modal is dismissed.
   */
  onDismissInProgress: () => void;
}

const MB_ModalUtilChildWrapper = memo((props: MB_ModalUtilChildWrapperPropsInternal) => {
  const onCloseButtonPressed = React.useCallback(() => {
    props.onDismissInProgress();
    props.onCloseButtonPressed?.();
  }, [props]);

  const Backdrop = useMemo(
    () => () => {
      return (
        <MB_ModalUtilBackdrop
          disableBackdropBlur={props.disableBackdropBlur}
          customBackdrop={props.customBackdrop}
          backdropBlurIntensity={props.backdropBlurIntensity}
          backdropBlurTint={props.backdropBlurTint}
          closeOnBackdropPress={props.closeOnBackdropPress}
          onBackdropPress={props.onBackdropPress}
          onDismissInProgress={props.onDismissInProgress}
          backdropColor={props.backdropColor}
          backdropOpacity={props.backdropOpacity}
        />
      );
    },
    [
      props.backdropBlurIntensity,
      props.backdropBlurTint,
      props.backdropColor,
      props.backdropOpacity,
      props.closeOnBackdropPress,
      props.customBackdrop,
      props.disableBackdropBlur,
      props.onBackdropPress,
      props.onDismissInProgress,
    ]
  );

  const styleForChildWrapperPadding: StyleProp<ViewStyle> = useMemo(() => {
    return {
      // 74 here is padding for title(14 + 12) + title height(28) + margin below the title(20)
      // 92 here is padding for title(18 + 18) + title height(32) + margin below the title(24)
      paddingTop: props.title ? (isMobile ? 74 : 92) : 54,
    };
  }, [props.title]);

  const SimplifiedBody = useMemo(() => {
    return (
      <>
        <Backdrop />
        <View style={[styles.childrenWrapper, styleForChildWrapperPadding, props.childrenWrapperStyle]}>
          {props.title || props.titleCustomComponent ? (
            <View style={[styles.titleWrapper, props.titleWrapperStyle]}>
              {props.titleCustomComponent ? props.titleCustomComponent : <Text style={[styles.title, props.titleStyle]}>{props.title}</Text>}
            </View>
          ) : null}

          {props.children}

          {props.closeButton ? (
            props.closeButton
          ) : (
            <MB_ModalUtilCloseButton
              onPressed={onCloseButtonPressed}
              closeButtonProps={props.closeButtonProps}
              hideCloseButton={props.hideCloseButton}
              xSize={props.closeButtonXProps?.size}
              xColor={props.closeButtonXProps?.color}
            />
          )}
        </View>
      </>
    );
  }, [
    Backdrop,
    onCloseButtonPressed,
    props.children,
    props.childrenWrapperStyle,
    props.closeButton,
    props.closeButtonProps,
    props.closeButtonXProps?.color,
    props.closeButtonXProps?.size,
    props.hideCloseButton,
    props.title,
    props.titleCustomComponent,
    props.titleStyle,
    props.titleWrapperStyle,
    styleForChildWrapperPadding,
  ]);

  const scrollViewContentContainerStyle = useMemo(
    () => StyleSheet.flatten([styles.scrollviewWrapper, props.scrollViewContentContainerStyle]),
    [props.scrollViewContentContainerStyle]
  );
  const simplifiedContainerStyle = useMemo(() => StyleSheet.flatten([styles.simplifiedChildrenWrapper, props.childrenWrapperStyle]), [props.childrenWrapperStyle]);

  if (props.simplified) {
    return (
      <SafeAreaView style={{ flex: 1 }}>
        <View style={simplifiedContainerStyle}>
          <Backdrop />
          {props.children}

          {props.closeButton ? (
            props.closeButton
          ) : (
            <MB_ModalUtilCloseButton onPressed={onCloseButtonPressed} closeButtonProps={props.closeButtonProps} hideCloseButton={props.hideCloseButton} />
          )}
        </View>
      </SafeAreaView>
    );
  }

  if (props.withoutScrollView) {
    return <SafeAreaView style={[styles.withoutScrollViewContainer, props.withoutScrollViewContainer]}>{SimplifiedBody}</SafeAreaView>;
  }

  return (
    <SafeAreaView style={{ flex: 1 }}>
      <MB_ModalUtilChildWrapperScrollView
        style={{ width: '100%' }}
        contentContainerStyle={scrollViewContentContainerStyle}
        extraScrollHeight={props.keyboardAwareProps?.extraScrollHeight}
        keyboardShouldPersistTaps={props.keyboardShouldPersistTaps}
      >
        {SimplifiedBody}
      </MB_ModalUtilChildWrapperScrollView>
    </SafeAreaView>
  );
});

export { MB_ModalUtilChildWrapper };
export type { MB_ModalUtilChildWrapperProps };

const styles = StyleSheet.create({
  withoutScrollViewContainer: {
    flex: 1,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  childrenWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: isMobile ? COLORS.darkBackground : COLORS.darkPurple,
    borderRadius: 10,
    marginVertical: 32,
    marginHorizontal: isMobile ? 16 : 32,
    paddingHorizontal: isMobile ? 12 : 24,
    paddingBottom: isMobile ? 16 : 18,
    overflow: 'hidden',
  },
  simplifiedChildrenWrapper: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  scrollviewWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
  },
  titleWrapper: {
    borderBottomWidth: 1,
    borderBottomColor: COLORS.englishVioletAlt,
    position: 'absolute',
    left: 0,
    right: 0,
    top: isMobile ? 14 : 18,
    paddingBottom: isMobile ? 12 : 18,
    paddingHorizontal: isMobile ? 36 : 44,
  },
  title: {
    fontWeight: '700',
    fontSize: isMobile ? 18 : 24,
    color: 'white',
    textAlign: 'center',
  },
});
